import React, {ReactElement} from "react";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import {
    ImageItem,
    isImageItem,
    isMarginItem, isPageBreakItem,
    isPageTitleItem,
    isTitleItem, isWineItem,
    MarginItem,
    MenuItem, PageBreakItem,
    PageTitleItem,
    TitleItem, WineItem
} from "../MenuDocumentModel";
import {PageTitleEditor} from "./PageTitleEditor";
import {TitleEditor} from "./TitleEditor";
import {MarginEditor} from "./MarginEditor";
import {EditorItem} from "../editorUtils";
import {PageBreakEditor} from "./PageBreakEditor";
import {ImageEditor} from "./image/ImageEditor";
import {FormatEditor} from "./FormatEditor";
import {WineEditor} from "./wine/WineEditor";
import { CoverPageEditor } from "./CoverPageEditor";
import {PositionControl} from "./PositionControl";
import { isLabelItem, LabelItem } from "features/label-editor/LabelDocumentModel";
import { LabelEditor } from "features/label-editor/LabelEditor";

export interface EditorSidebarProps {

}

export function EditorSidebar(props: EditorSidebarProps): ReactElement {
    const dispatch = useAppDispatch();
    const activeItem = useAppSelector(menuEditorSelectors.focusedItem);
    const unfocus = () => dispatch(menuEditorActions.unfocusActiveItem());

    const formatEditorOpen = useAppSelector(menuEditorSelectors.formatOptionsActive);
    const closeFormatEditor = () => dispatch(menuEditorActions.setFormatOptionsActive(false));

    const coverPageEditorOpen = useAppSelector(menuEditorSelectors.coverPageEditorActive);
    const closeCoverPageEditor = () => dispatch(menuEditorActions.setCoverPageEditorActive(false));

    return (
        <div className="py-5 relative">
            {formatEditorOpen && (<>
                <button className="absolute top-6 right-5 text-secondary"
                        onClick={closeFormatEditor}>
                    <CloseIcon className="w-4 h-4" />
                </button>

                <FormatEditor />
            </>)}

            {coverPageEditorOpen && (<>
                <button className="absolute top-6 right-5 text-secondary"
                        onClick={closeCoverPageEditor}>
                    <CloseIcon className="w-4 h-4" />
                </button>

                <CoverPageEditor />
            </>)}

            {activeItem != null && !formatEditorOpen && (<>
                <button className="absolute top-6 right-5 text-secondary"
                        onClick={unfocus}>
                    <CloseIcon className="w-4 h-4" />
                </button>

                <ItemEditor item={activeItem} />
            </>)}
        </div>
    );
}

interface ItemEditorProps {
    item: EditorItem<MenuItem>
}

export function ItemEditor(props: ItemEditorProps): ReactElement | null {
    const document = useAppSelector(menuEditorSelectors.document);

    if (isPageTitleItem(props.item.menuItem)) {
        return <PageTitleEditor title={props.item as EditorItem<PageTitleItem>}/>
    }
    let editor: ReactElement|null = null;

    if (isTitleItem(props.item.menuItem)) {
        editor = <TitleEditor title={props.item as EditorItem<TitleItem>}/>
    }
    else if (isMarginItem(props.item.menuItem)) {
        editor = <MarginEditor margin={props.item as EditorItem<MarginItem>}/>
    }
    else if (isPageBreakItem(props.item.menuItem)) {
        editor = <PageBreakEditor pageBreak={props.item as EditorItem<PageBreakItem>}/>
    }
    else if (isImageItem(props.item.menuItem)) {
        editor = <ImageEditor image={props.item as EditorItem<ImageItem>}/>
    }
    else if (isWineItem(props.item.menuItem)) {
        editor = <WineEditor wine={props.item as EditorItem<WineItem>}/>
    }
    else if (isLabelItem(props.item.menuItem)) {
        editor = <LabelEditor labelItem={props.item as EditorItem<LabelItem>}/>
    }
    else {
        return null;
    }

    return <>
        {document?.type==='wine'&&<PositionControl itemKey={props.item.itemKey}/>}
        {editor}
    </>;
}